import _ from "lodash";
import React from "react";
import classes from "./LinkModal.scss";
import Loading from "UIComponents/Loading";
import UIModal from "UIComponents/UIModal";
import I18nHOC from "UIComponents/I18nHOC";
import { getWebLinkDetails } from "modules/Services";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";
import classNames from "classnames";
import update from "immutability-helper";
import { isURL, addHttpsInUrl, generateRandomId } from "Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  Button,
  FormInputError,
  IconButton,
  TextAreaInput,
  Modal,
  TextInput,
} from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";

const DEFAULT_MODAL_STYLES = {
  header: {},
  body: {},
  footer: {},
};

const styles = {
  title: {
    overflowY: "hidden",
  },

  textAreaStyles: {
    overflowY: "hidden",
  },
};

const YOUTUBE_URL_VALIDATION_RE = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;

// * Checking is valid Youtube URL
const isYoutubeURL = url => {
  const match = url.match(YOUTUBE_URL_VALIDATION_RE);
  if (match && _.isEqual(_.get(match, "[2].length"), 11)) {
    return match[2];
  } else {
    return false;
  }
};

// * Extraction Youtube details from embed URL
const getYoutubeDetails = async url => {
  const match = url.match(YOUTUBE_URL_VALIDATION_RE);
  if (match && _.isEqual(_.get(match, "[2].length"), 11)) {
    const youtubeEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${match[2]}&format=json`;
    const res = await fetch(youtubeEmbedUrl);
    if (res.ok) {
      const out = await res.json();
      if (out) {
        return {
          attachments: [
            {
              type: "VIDEO",
              name: _.get(out, "title", "Youtube video"),
              description: "Youtube video",
              url: url,
              mimeType: null,
            },
          ],
        };
      }
    }
  }
  return null;
};

class LinkModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.errorId = _.uniqueId("form-error-");

    this.state = {
      link: _.get(props, "value", ""),
      isLoading: false,
      error: "",
      item: _.get(props, "item", {}),
    };

    props.customRef?.(this);
  }

  componentWillUnmount() {
    const { customRef } = this.props;
    customRef?.(null);
  }

  onLinkAdd = async () => {
    const {
      getWebLinkDetails,
      onLinkAdd,
      onUpdateLinkModal,
      t,
      onClose,
      setToastMsg,
      mode,
      shouldExtractYoutubeDetails,
    } = this.props;

    const { link, item } = this.state;

    this.setState({ isLoading: true });
    const linkValue = _.trim(link);
    if (isURL(linkValue)) {
      const linkUrl = addHttpsInUrl(linkValue);
      let urlDetailObject = null;

      // * if Youtube URL, extract title from it if `shouldExtractYoutubeDetails = true`
      if (shouldExtractYoutubeDetails && isYoutubeURL(linkUrl)) {
        const youtubeVideoDetail = await getYoutubeDetails(linkUrl);
        if (youtubeVideoDetail) {
          urlDetailObject = youtubeVideoDetail;
        }
      }

      if (!urlDetailObject) urlDetailObject = await getWebLinkDetails(linkUrl);
      const titleValue = _.get(item, "titleValue", "");
      if (urlDetailObject) {
        if (titleValue != "") {
          urlDetailObject.attachments[0].name = titleValue;
        }
        const id = generateRandomId();

        mode == "edit"
          ? onUpdateLinkModal({
              attachments: urlDetailObject.attachments,
              item: item,
            })
          : await onLinkAdd({ attachments: urlDetailObject.attachments, id });

        this.setState({ link: "", item: {}, isLoading: false });
        onClose();
      } else {
        this.setState({
          error: t("common:upload_err"),
          isLoading: false,
        });
      }
    } else {
      setToastMsg("toastMsgs:web_link_not_valid");
      this.setState({
        error: t("common:invalid_link"),
        isLoading: false,
      });
    }
  };

  updateLink = e => {
    const link = e.target.value;
    const { t, validateOnUserInput = false } = this.props;
    if (validateOnUserInput) {
      if (!isURL(link)) {
        this.setState({
          error: t("common:invalid_link"),
        });
      } else {
        this.setState({
          error: "",
        });
      }
    }
    this.setState({ link });
  };

  updateTitle = e => {
    const titleValue = e.target.value;
    this.setState(prevState => ({
      item: update(prevState.item, {
        titleValue: { $set: titleValue },
      }),
    }));
  };

  onKeyUp = e => {
    if (e.key === "Enter" || e.keyCode === 13) {
      this.onLinkAdd();
    }
  };

  onKeyDown = e => {
    e.stopPropagation();

    if (e.key === "Enter" || e.keyCode === 13) {
      this.onLinkAdd();
    }
  };

  render() {
    const {
      t,
      mode,
      onClose,
      testId,
      showDisplayText,
      isGlobalHeaderOnMultipleScreensEnabled,
      shouldCloseOnOverlayClick,
      dsVersion,
      parentType,
      modalStyles,
      validateOnUserInput = false,
    } = this.props;
    const { link, isLoading, error, item } = this.state;
    const showTitleInput =
      ACLStore.can("FeatureFlag:EnableLinkTitle") && showDisplayText;
    const modalContent = classNames(
      { [classes.modalContentWithTitle]: showTitleInput },
      { [classes.modalContentWithoutTitle]: !showTitleInput },
      { [classes.modalContentWithError]: error && showTitleInput }
    );
    const modalTitle =
      mode == "create" ? t("common:add_link") : t("workbook:edit_link");

    const isParentTypeAIConversation = parentType === "AI_CONVERSATION";

    const isDsV2 = dsVersion === "2.0";

    const modalBodyClassName = classNames(classes.modalBodyV2, {
      [classes.modalBodyV2WithoutTitle]: !showTitleInput,
    });

    const isSubmitButtonDisabled =
      isLoading || (validateOnUserInput && (error || !link));
    if (isDsV2) {
      return (
        <Modal
          width="480px"
          height="fit-content"
          dsVersion="2.0"
          isOpen={true}
          onClose={onClose}
        >
          <Modal.Header title={modalTitle} style={modalStyles.header} />
          <Modal.Body style={modalStyles.body}>
            <div className={modalBodyClassName}>
              {isLoading && !isParentTypeAIConversation ? (
                <Loading isMiniLoader={isDsV2} />
              ) : (
                <>
                  {showTitleInput ? (
                    <div>
                      <div className={classNames("text-label", classes.label)}>
                        {t("common:display_text")}
                      </div>
                      <TextInput
                        testId={`${testId}-linkModal-displayName`}
                        placeholder={t("common:enter_text_here")}
                        onChange={this.updateTitle}
                        name="title"
                        value={item.titleValue}
                        style={styles.title}
                        dsVersion="2.0"
                        onKeyDown={this.onKeyDown}
                      />
                    </div>
                  ) : null}
                  <div>
                    <div className={classNames("text-label", classes.label)}>
                      {t("common:link")}*
                    </div>
                    <TextInput
                      testId={`${testId}-linkModal-link`}
                      placeholder={t("common:enter_link_here")}
                      onChange={this.updateLink}
                      name="link"
                      value={link}
                      error={error}
                      style={styles.textAreaStyles}
                      dsVersion="2.0"
                      disabled={isLoading}
                      onKeyDown={this.onKeyDown}
                      aria-describedby={`linkType-${this.errorId}`}
                    />
                    <FormInputError
                      id={`linkType-${this.errorId}`}
                      dsVersion="2.0"
                      {...(!_.isEmpty(error) && {
                        className: classes.formInputError,
                        error,
                      })}
                    />
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer style={modalStyles.footer}>
            <div className={classes.footerContainer}>
              <div className={classes.loaderContainer}>
                {isLoading && isParentTypeAIConversation ? (
                  <div className={classes.analysisLoaderContainer}>
                    <div className={classes.dot} />
                    <div className="text-label-3">
                      {t("common:processing_no_dots")}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className={classes.buttonsContainer}>
                <Button
                  dsVersion="2.0"
                  size="medium"
                  onClick={onClose}
                  testId={`${testId}-linkModal-cancel`}
                  variant="neutral"
                  type={"outlined"}
                  shouldStopPropagation={true}
                  onKeyDown={this.onKeyDown}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="progressive"
                  testId={`${testId}-linkModal-submit`}
                  onClick={this.onLinkAdd}
                  disabled={isSubmitButtonDisabled}
                  type="fill"
                  shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                  shouldStopPropagation={true}
                  onKeyDown={this.onKeyDown}
                >
                  {mode === "edit" ? t("common:update") : t("common:done")}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
      );
    }

    return (
      <UIModal
        isOpen={true}
        onRequestClose={onClose}
        modalContent={modalContent}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      >
        <div className={classes.modalHeader}>
          <div className={classes.header}>{modalTitle}</div>
          <IconButton
            icon={<CloseOutlined />}
            variant="plain"
            size="medium"
            testId={`${testId}-linkModal-close`}
            onClick={onClose}
          />
        </div>

        <div className={classes.modalBody}>
          {isLoading ? (
            <Loading />
          ) : (
            <React.Fragment>
              <div>
                <TextAreaInput
                  minRows={1}
                  testId={`${testId}-linkModal-link`}
                  placeholder={t("common:enter_link_url_placeholder")}
                  onChange={this.updateLink}
                  name="link"
                  value={link}
                  error={error}
                  onKeyUp={this.onKeyUp}
                  style={styles.textAreaStyles}
                  label={t("common:link")}
                  onKeyDown={this.onKeyDown}
                  aria-describedby={`embeddedLink-${this.errorId}`}
                />
                <FormInputError
                  id={`embeddedLink-${this.errorId}`}
                  {...(!_.isEmpty(error) && {
                    className: classes.formInputError,
                    error,
                  })}
                />
              </div>
              {showTitleInput ? (
                <TextAreaInput
                  minRows={1}
                  testId={`${testId}-linkModal-displayName`}
                  placeholder={t("common:add_display_text_for_link")}
                  onChange={this.updateTitle}
                  name="title"
                  value={item.titleValue}
                  onKeyUp={this.onKeyUp}
                  style={styles.title}
                  label={t("common:display_text")}
                  onKeyDown={this.onKeyDown}
                />
              ) : null}
            </React.Fragment>
          )}
        </div>
        <div className={classes.modalFooter}>
          <Button
            dsVersion={isGlobalHeaderOnMultipleScreensEnabled ? "2.0" : "1.0"}
            size="large"
            onClick={onClose}
            testId={`${testId}-linkModal-cancel`}
            style={{ marginRight: "12px" }}
            variant={
              isGlobalHeaderOnMultipleScreensEnabled
                ? "neutral"
                : "outlined-subtle"
            }
            type={"outlined"}
            shouldStopPropagation={true}
            onKeyDown={this.onKeyDown}
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion={isGlobalHeaderOnMultipleScreensEnabled ? "2.0" : "1.0"}
            size="large"
            variant="progressive"
            testId={`${testId}-linkModal-submit`}
            onClick={this.onLinkAdd}
            disabled={isLoading}
            type={"fill"}
            shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
            shouldStopPropagation={true}
            onKeyDown={this.onKeyDown}
          >
            {mode === "edit" ? t("common:update") : t("common:done")}
          </Button>
        </div>
      </UIModal>
    );
  }
}

LinkModal.defaultProps = {
  value: "",
  mode: "create",
  testId: "UIComponents",
  id: "",
  item: {
    titleValue: "",
  },
  showDisplayText: true,
  shouldCloseOnOverlayClick: true,
  dsVersion: "1.0",
  modalStyles: DEFAULT_MODAL_STYLES,
  shouldExtractYoutubeDetails: false,
};

const mapActionCreators = {
  getWebLinkDetails,
  setToastMsg,
};

const mapStateToProps = (state, ownProps) => {
  return {};
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: "common" })
)(LinkModal);
